import { isEmpty, isNil } from 'lodash';

import useIdBasedPagination from '~/hooks/useIdBasedPagination';
import type {
  WorkflowRuleItemIdsParams,
  WorkflowRuleItemsByIdsParams,
  WorkflowRuleModel,
} from '~/server_shared/openapi/data-contracts';

const DEFAULT_ROWS_PER_PAGE = 25;
const FETCH_IDS_URL = '/api/v1/automation/workflow_engine/rules';
const FETCH_PAGE_DATA_URL = '/api/v1/automation/workflow_engine/rules';

interface UseWorkflowRulesPaginationProps {
  organizationId: number;
  initialRowsPerPage?: number;
  sortByColumn?: string; // e.g., 'display_name', '+event_key', '-created_datetime'

  // Filters - updated to support server-side filtering
  subOrganizationIds?: number[] | null; // Changed from single ID to array
  lobs?: string[] | null; // Added LOB filtering
  searchTerm?: string | null; // Added search term filtering
  eventKey?: string | null;
  workflowId?: string | null;
  active?: boolean | null;
}

export const useWorkflowRulesPagination = ({
  organizationId,
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  sortByColumn = '-created_datetime', // Default sorting
  subOrganizationIds,
  lobs,
  searchTerm,
  eventKey,
  workflowId,
  active,
}: UseWorkflowRulesPaginationProps) => {
  // Build the request payload for the IDs endpoint using proper typed interface
  const fetchIdsParams: WorkflowRuleItemIdsParams = {
    sort_by: sortByColumn,
    // Include filters only if they have values
    ...(!isEmpty(subOrganizationIds) && { sub_organization_ids: subOrganizationIds }),
    ...(!isEmpty(lobs) && { lobs }),
    ...(!isEmpty(searchTerm?.trim()) && { search_term: searchTerm }),
    ...(!isNil(eventKey) && { event_key: eventKey }),
    ...(!isNil(workflowId) && { workflow_id: workflowId }),
    ...(!isNil(active) && { active }),
  };

  // Build the request payload for the page data endpoint using proper typed interface
  // Note: IDs are handled by the pagination hook, so we omit them from the type
  const fetchPageDataParams: Omit<WorkflowRuleItemsByIdsParams, 'ids'> = {
    include_configuration: true,
  };

  // Build URLs with organization ID
  const fetchIdsUrl = `${FETCH_IDS_URL}/${organizationId}/item_ids`;
  const fetchPageDataUrl = `${FETCH_PAGE_DATA_URL}/${organizationId}/items_by_ids`;

  return useIdBasedPagination<string, null, WorkflowRuleModel, null>({
    fetchIdsUrl,
    fetchPageDataUrl,
    fetchIdsParams: fetchIdsParams as unknown as Record<string, unknown>,
    initialRowsPerPage,
    fetchPageDataParams: fetchPageDataParams as unknown as Record<string, unknown>,
  });
};
